import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyPaymentScheduleKey,
  ShopifyPaymentScheduleToPaymentTermsKey,
  ShopifyPaymentScheduleToShopKey,
  ShopifyPaymentTermsKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyPaymentScheduleKey,
  "2024-01": {
    name: "Shopify Payment Schedule",
    graphQLTypename: "PaymentSchedule",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id", shopifyFieldKey: "id" } },
      },
      {
        name: "Completed At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "completedAt", shopifyFieldKey: "completed_at" } },
      },
      {
        name: "Issued At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "issuedAt", shopifyFieldKey: "issued_at" } },
      },
      {
        name: "Due At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "dueAt", shopifyFieldKey: "due_at" } },
      },
      // this field is deprecated in the graphql api
      {
        name: "Amount",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "amount",
            graphQLFieldKey: "amount",
            graphQLSelection: `
            amount
            currencyCode
            `,
            deprecated: true,
          },
        },
      },

      // these fields exist in the rest api only and not graphql, and so we infer that they will be deprecated
      {
        name: "Currency",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "currency", graphQLFieldKey: "currency", skipGraphQLSelection: true, deprecated: true },
        },
      },
      {
        name: "Expected Payment Method",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "expected_payment_method", deprecated: true },
        },
      },

      {
        name: "Payment Terms",
        fieldType: FieldType.BelongsTo,
        key: ShopifyPaymentScheduleToPaymentTermsKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "paymentTerms",
            graphQLSelection: "id",
            shopifyFieldKey: "payment_terms_id",
          },
        },
        configuration: {
          relatedModelKey: ShopifyPaymentTermsKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyPaymentScheduleToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: ["order", "draftOrders"],
      resource: ["order.paymentSchedules", "draftOrder.paymentSchedules"],
    },
  },
  "2025-04": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "id",
            shopifyFieldKey: "id",
            availableInWebhook: ["payment_terms/create", "payment_terms/update"],
          },
        },
      },
      {
        name: "Completed At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "completedAt",
            shopifyFieldKey: "completed_at",
            availableInWebhook: ["payment_terms/create", "payment_terms/update"],
          },
        },
      },
      {
        name: "Issued At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "issuedAt",
            shopifyFieldKey: "issued_at",
            availableInWebhook: ["payment_terms/create", "payment_terms/update"],
          },
        },
      },
      {
        name: "Due At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "dueAt",
            shopifyFieldKey: "due_at",
            availableInWebhook: ["payment_terms/create", "payment_terms/update"],
          },
        },
      },
      {
        name: "Payment Terms",
        fieldType: FieldType.BelongsTo,
        key: ShopifyPaymentScheduleToPaymentTermsKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "paymentTerms",
            graphQLSelection: "id",
            shopifyFieldKey: "payment_terms_id",
            availableInWebhook: ["payment_terms/create", "payment_terms/update"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyPaymentTermsKey,
        },
      },
      // this field is deprecated in the graphql api
      {
        name: "Amount",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "amount",
            availableInWebhook: ["payment_terms/create", "payment_terms/update"],
            graphQLFieldKey: "amount",
            graphQLSelection: `
            amount
            currencyCode
            `,
            deprecated: true,
          },
        },
      },
      // these fields exist in the rest api only and not graphql, and so we infer that they will be deprecated
      {
        name: "Currency",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "currency",
            graphQLFieldKey: "currency",
            skipGraphQLSelection: true,
            deprecated: true,
            availableInWebhook: ["payment_terms/create", "payment_terms/update"],
          },
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: ["orders", "draftOrders"],
      resource: ["order.paymentSchedules", "draftOrder.paymentSchedules"],
    },
  },
  "2025-10": {
    fields: [
      {
        name: "Balance Due",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "balanceDue",
            shopifyFieldKey: "balance_due",
            graphQLSelection: `
            amount
            currencyCode
            `,
            availableInWebhook: ["payment_terms/create", "payment_terms/update"],
          },
        },
      },
      {
        name: "Total Balance",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "totalBalance",
            shopifyFieldKey: "total_balance",
            graphQLSelection: `
            amount
            currencyCode
            `,
            availableInWebhook: ["payment_terms/create", "payment_terms/update"],
          },
        },
      },
      {
        name: "Due",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "due",
          },
        },
      },
    ],
  },
};

export const ShopifyPaymentSchedule = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
